import { Directive, inject, TemplateRef } from '@angular/core';

/**
 * Template context of an `immoEntitySelectRow` template.
 */
export interface EntitySelectRowContext<TOption> {

    /** The option rendered by this row. */
    $implicit: TOption;

    /** Whether the option is the currently selected one. */
    selected: boolean;

    /** Whether the option is the currently active (keyboard-highlighted) one. */
    active: boolean;
}

/**
 * Marks an `<ng-template>` as the custom dropdown row of an `immo-entity-select`.
 *
 * The template receives the option as `$implicit` plus `selected` / `active` flags,
 * so custom rows can style their own selection state instead of the default highlight.
 *
 * ```html
 * <immo-entity-select [field]="form().object" [source]="dataSource">
 *     <ng-template immoEntitySelectRow let-option let-selected="selected">
 *         <immo-object-list-cell [entity]="option" [highlighted]="selected" />
 *     </ng-template>
 * </immo-entity-select>
 * ```
 */
@Directive({
    selector: '[immoEntitySelectRow]',
})
export class EntitySelectRowDirective<TOption = unknown> {

    public template = inject(TemplateRef<EntitySelectRowContext<TOption>>);

    static ngTemplateContextGuard<TOption>(
        directive: EntitySelectRowDirective<TOption>,
        context: unknown,
    ): context is EntitySelectRowContext<TOption> {
        return true;
    }
}
